<template>
  <!--
    DO IT YOUR WAY (spec 53 section 15, Mark 2026-11-02): a training run that
    gives the task and a checklist, and lets the person work their own way.

    Mark ran Can we take this load? as someone who knows the job, faster than
    the cards: he did it all while the walkthrough still waited on its first
    card, pressed Skip to get to the end, and was told he had skipped
    everything. So the default run is this panel. Every graded step is
    watched from the start, in any order, on any page (one action run per
    step, tours/actionRun.js, the same watcher a card uses); a line ticks
    when its steps are met; "Show me" does a line the way "Do this step for
    me" does; Finish hands the log to the grader as the last card does.
    "Guide me step by step" moves to the cards (TourHost) and they keep the
    log. A practice fleet only: nothing here runs on a real tenant or the
    sales demo.
  -->
  <section
    v-if="active && !onVideoPage"
    class="lw-c-task"
    :class="{ 'lw-c-task--collapsed': collapsed }"
    role="region"
    :aria-label="`Training: ${title}`"
    data-test="task-panel"
  >
    <header class="lw-c-task__head">
      <p class="lw-c-task__eyebrow">
        <span>Practice</span>
        <span data-test="task-count">{{ doneCount }} of {{ gradedCount }} done</span>
      </p>
      <h2 class="lw-c-task__title">{{ title }}</h2>
      <button
        type="button"
        class="lw-c-task__toggle"
        :aria-expanded="String(!collapsed)"
        :aria-label="collapsed ? 'Show the task' : 'Hide the task'"
        data-test="task-toggle"
        @click="collapsed = !collapsed"
      >
        <i :class="collapsed ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" aria-hidden="true" />
      </button>
    </header>

    <div v-show="!collapsed" class="lw-c-task__body">
      <!--
        The task is read once, then folds away when the person starts moving
        about, so the panel is the checklist and covers as little of the page
        as it can (it sat over Check drivers, 2026-11-02).
      -->
      <p v-if="task && taskOpen" class="lw-c-task__task" data-test="task-text">{{ task }}</p>
      <p v-if="taskOpen" class="lw-c-task__how">Do it your way. Each line ticks when it's done.</p>
      <!-- Spec 53 15.5: the video is a click away, and each line's part of it. -->
      <div v-if="(task && !taskOpen) || hasVideo" class="lw-c-task__links">
        <LwButton
          v-if="task && !taskOpen"
          variant="link"
          size="sm"
          data-test="task-reopen"
          @click="taskOpen = true"
        >
          The task
        </LwButton>
        <LwButton
          v-if="hasVideo"
          variant="link"
          size="sm"
          data-test="task-watch"
          @click="watchVideo()"
        >
          Watch the video
        </LwButton>
      </div>

      <ol class="lw-c-task__list">
        <li
          v-for="line in checklist"
          :key="line.key"
          class="lw-c-task__line"
          :class="{ 'lw-c-task__line--done': line.done }"
          :data-test="`task-line-${line.key}`"
        >
          <span class="lw-c-task__mark" aria-hidden="true">
            <i v-if="line.done" class="fa fa-check" />
          </span>
          <div class="lw-c-task__line-text">
            <p class="lw-c-task__line-title">
              {{ line.title }}<span class="lw-c-task__sr">{{ line.done ? ': done' : '' }}</span>
            </p>
            <p v-if="line.hint && !line.done" class="lw-c-task__hint">{{ line.hint }}</p>
            <p v-if="line.done && line.shown" class="lw-c-task__hint">Shown by Relay.</p>
            <div v-if="!line.done" class="lw-c-task__line-actions">
              <LwButton
                v-if="line.drive"
                size="sm"
                :disabled="driveState === 'sending' || driveState === 'driving'"
                :data-test="`task-drive-${line.key}`"
                @click="sendDriver"
              >
                Send the driver
              </LwButton>
              <LwButton
                v-if="hasVideo && line.stepIds.length"
                variant="link"
                size="sm"
                :data-test="`task-watch-${line.key}`"
                @click="watchVideo(line)"
              >
                Watch how
              </LwButton>
              <LwButton
                v-if="line.stepIds.length && canShow(line)"
                variant="link"
                size="sm"
                :disabled="Boolean(showingKey)"
                :data-test="`task-show-${line.key}`"
                @click="showMe(line)"
              >
                Show me
              </LwButton>
            </div>
          </div>
        </li>
      </ol>

      <p v-if="statusLine" class="lw-c-task__status" data-test="task-status">{{ statusLine }}</p>

      <div class="lw-c-task__foot">
        <LwButton variant="link" size="sm" data-test="task-leave" @click="leave">Leave</LwButton>
        <LwButton variant="link" size="sm" data-test="task-guided" @click="guideMe">
          Guide me step by step
        </LwButton>
        <LwButton v-if="finished" size="sm" data-test="task-finish" @click="finish">
          Finish
        </LwButton>
      </div>
    </div>

    <p class="lw-c-task__live" aria-live="polite">{{ announcement }}</p>
  </section>
</template>

<script>
import { nextTick } from 'vue';
import LwButton from './lw/LwButton.vue';
import { flattenSteps } from '../tours/engine';
import { audienceOf, loadTour, TOUR_IDS } from '../tours/registry';
import { clearStory, rememberContext } from '../tours/persistence';
import { prepareContext } from '../tours/prepareContext';
import { targetSelector } from '../tours/geometry';
import { createRequestLog, waitForElement } from '../tours/waitFor';
import { createActionRun } from '../tours/actionRun';
import { fill } from '../tours/context';
import { grade } from '../training/grade';
import { kindOf, moduleOf } from '../training/kinds';
import { readRun, record, refuse, writeRun } from '../training/stepLog';
import { readCompletions } from '../lib/checklistTrail';
import { renderer } from '../training/render';
import { allDone, forWatching, linesOf, tickedLines, watchable } from '../training/taskMode';

const SLUG = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
// The pace Show me types at, as the cards' fallback does; none under reduced motion.
const CADENCE_MS = 45;
const GAP_MS = 350;
// How long Show me waits on the practice server hearing of it before acting anyway.
const ASSIST_WAIT_MS = 5000;
// How long a page Show me moved to gets to draw before it acts on it.
const SETTLE_MS = 400;
// The simulated driver (spec 53 5.5): how often the delivery is looked for,
// and how long past the server's estimate before saying something is wrong.
const DRIVE_POLL_MS = 5000;
const DRIVE_GRACE_MS = 60000;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export default {
  name: 'TaskHost',
  components: { LwButton },
  data() {
    return {
      active: false,
      collapsed: false,
      taskOpen: true,
      definition: null,
      steps: [],
      log: [],
      context: {},
      announcement: '',
      statusLine: '',
      showingKey: null,
      driveState: null,
      // A published video for this module (GET /training/modules/:id/video).
      hasVideo: false,
    };
  },
  computed: {
    /** A practice fleet: a demo lease that carries a training enrollment. */
    practice() {
      const getters = (this.$store && this.$store.getters) || {};
      return Boolean(getters.isDemo && getters.trainingLease) && !renderer();
    },
    title() {
      return (this.definition && this.definition.title) || '';
    },
    /** The task in a sentence or two: `training.task`, else the intro. */
    task() {
      const training = (this.definition && this.definition.training) || {};
      const said = training.task || (training.narration && training.narration.intro) || '';
      return fill(said, this.context);
    },
    lines() {
      return linesOf(this.steps, this.context);
    },
    /**
     * The lines, ticked. A line that only sends the simulated driver (Deliver)
     * has nothing graded on it: it ticks when the delivery is in.
     */
    checklist() {
      return tickedLines(this.lines, this.log).map(line =>
        line.stepIds.length ? line : { ...line, done: this.driveState === 'delivered' }
      );
    },
    gradedCount() {
      return this.checklist.filter(line => line.stepIds.length).length;
    },
    doneCount() {
      return this.checklist.filter(line => line.stepIds.length && line.done).length;
    },
    finished() {
      return allDone(this.checklist);
    },
    /** The module's video page: the run keeps going, the panel steps aside. */
    onVideoPage() {
      return Boolean(this.$route && this.$route.name === 'TrainingModule');
    },
  },
  watch: {
    practice(now) {
      if (now) this.maybeStart();
    },
    '$route.query.task'(task) {
      if (!task) return;
      // Start over (the video page's Practice while a run is going): the new
      // run replaces this one.
      if (this.active) this.stop();
      this.maybeStart();
    },
    '$route.path'() {
      if (!this.active) return;
      // A Find step whose element was already on the page when the run began
      // waits for the person to go somewhere first (armAll).
      if (!this.moved) this.taskOpen = false;
      this.moved = true;
      this.armAll();
    },
    finished(now, before) {
      if (now && !before && this.active) {
        this.announcement = 'Every line is done. Press Finish when you are ready.';
      }
    },
  },
  created() {
    // Not reactive on purpose: live watchers, an HTTP interceptor, ids.
    this.runs = new Map();
    this.showing = new Set();
    this.presentAtStart = new Set();
    this.requests = null;
    this.module = null;
    this.startedAt = null;
    this.captured = {};
    this.moved = false;
    this.starting = false;
    this.loadIdCache = {};
    this.driveTimer = null;
  },
  mounted() {
    this.maybeStart();
  },
  beforeUnmount() {
    this.stop();
  },
  methods: {
    /**
     * Start from `?task=<id>` (training/practise.js), or pick up this tab's
     * free run after a reload. Only a training definition, only on a
     * practice fleet.
     */
    async maybeStart() {
      if (this.active || this.starting || !this.practice) return;
      const query = (this.$route && this.$route.query) || {};
      const asked =
        typeof query.task === 'string' && SLUG.test(query.task) && TOUR_IDS.includes(query.task)
          ? query.task
          : null;
      const saved = readRun();
      const again =
        saved && saved.mode === 'free' && !saved.finished && !saved.handover && !saved.left
          ? saved.tourId
          : null;
      const id = asked || again;
      if (!id) return;
      this.starting = true;
      try {
        const definition = await loadTour(id);
        if (!definition || definition.kind !== 'task' || !definition.training) return;
        if (!audienceOf(definition).includes('training')) return;
        const resuming = Boolean(
          saved && saved.tourId === id && saved.mode === 'free' && !saved.finished && !asked
        );
        const lease = this.$store.getters.trainingLease;
        const context = await prepareContext({
          http: this.$http,
          definition,
          resuming,
          lease,
        });
        const user = this.$store.getters.user || null;

        this.definition = definition;
        this.module = moduleOf(definition);
        this.context = context || {};
        this.steps = flattenSteps(definition, user && user.role, this.context);
        if (resuming && saved.version === definition.version) {
          this.log = saved.steps;
          this.startedAt = saved.startedAt;
          this.captured = saved.captured;
          this.context = { ...this.context, ...saved.captured };
        } else {
          this.log = [];
          this.startedAt = new Date().toISOString();
          this.captured = {};
        }
        this.moved = false;
        this.collapsed = false;
        this.taskOpen = true;
        this.statusLine = '';
        this.active = true;
        this.save();
        // The cards this tab was in the middle of, if any, are over: one run
        // at a time, and TourHost picks up nothing while this one is going.
        clearStory();

        if (asked && typeof this.$router.replace === 'function') {
          const rest = { ...query };
          delete rest.task;
          Promise.resolve(this.$router.replace({ path: this.$route.path, query: rest })).catch(
            () => {}
          );
        }

        this.requests = createRequestLog(this.$http);
        this.requests.install();
        this.askVideo();
        await nextTick();
        this.notePresent();
        this.armAll();
        this.announcement = `${this.title}. ${this.task}`;
      } finally {
        this.starting = false;
      }
    },

    /** Whether the module has a video to go back to; never in the way if not. */
    async askVideo() {
      this.hasVideo = false;
      const id = this.module && this.module.id;
      try {
        const res = await this.$http.get(`/training/modules/${encodeURIComponent(id)}/video`);
        const video = res && res.data && res.data.video;
        if (this.module && this.module.id === id) this.hasVideo = Boolean(video && video.url);
      } catch (err) {
        this.hasVideo = false;
      }
    },

    /**
     * The module's video page, with the way back (`?back=`, this page) and,
     * from a line, `?at=` its card's title, which names the chapter that
     * teaches it (TrainingModule seeks there). The run keeps going.
     */
    watchVideo(line = null) {
      if (!this.module) return;
      const query = { back: this.$route.fullPath };
      if (line && line.title) query.at = line.title;
      Promise.resolve(
        this.$router.push({ name: 'TrainingModule', params: { id: this.module.id }, query })
      ).catch(() => {});
    },

    stepById(id) {
      return this.steps.find(step => step.id === id) || null;
    },

    isMet(id) {
      return this.log.some(e => e.id === id && (e.mode === 'self' || e.mode === 'shown'));
    },

    /**
     * Find steps whose element is on the page already (the starting page
     * shows it): they count only once the person has gone somewhere, so
     * arriving is not mistaken for doing.
     */
    notePresent() {
      this.presentAtStart = new Set();
      for (const line of this.lines) {
        for (const id of line.stepIds) {
          const step = watchable(this.stepById(id), this.context);
          if (!step || kindOf(step) !== 'find') continue;
          if (document.querySelector(targetSelector(step.until.element))) {
            this.presentAtStart.add(id);
          }
        }
      }
    },

    /** Watch every graded step not yet met whose tokens are known. */
    armAll() {
      if (!this.active) return;
      for (const line of this.lines) line.stepIds.forEach(id => this.arm(id));
    },

    arm(id, { now = false } = {}) {
      if (!this.active || this.runs.has(id) || this.isMet(id)) return;
      const step = watchable(this.stepById(id), this.context);
      if (!step) return;
      if (!now && this.presentAtStart.has(id) && !this.moved) return;
      const reduced = prefersReducedMotion();
      const run = createActionRun({
        step: forWatching(step),
        requests: this.requests,
        selector: targetSelector,
        find: target => waitForElement(targetSelector(target), { timeoutMs: step.waitMs || 6000 }),
        location: () => this.$route,
        cadenceMs: reduced ? 0 : CADENCE_MS,
        gapMs: reduced ? 0 : GAP_MS,
        onPhase: phase => {
          if (phase === 'playing') this.statusLine = 'Relay is doing it...';
        },
      });
      this.runs.set(id, run);
      run.result.then(result => this.onResult(id, step, run, result));
    },

    onResult(id, step, run, result) {
      if (this.runs.get(id) !== run) return;
      this.runs.delete(id);
      if (!this.active) return;
      if (result.outcome === 'done') {
        const mode = this.showing.has(id) ? 'shown' : 'self';
        if (result.captured && Object.keys(result.captured).length) {
          this.context = { ...this.context, ...result.captured };
          this.captured = { ...this.captured, ...result.captured };
          rememberContext(result.captured);
        }
        this.log = record(this.log, { id, mode, title: step.field || step.title });
        this.save();
        const line = this.checklist.find(l => l.stepIds.includes(id));
        if (line && line.done) this.announcement = `Done: ${line.title}.`;
        this.armAll();
      } else if (result.outcome === 'refused') {
        // The server said no. Logged, never failed; the step stays open.
        this.log = refuse(this.log, { id, title: step.field || step.title });
        this.save();
        this.arm(id, { now: true });
      } else if (result.outcome === 'timeout') {
        this.arm(id, { now: true });
      }
    },

    canShow(line) {
      return line.stepIds.some(id => {
        const step = this.stepById(id);
        return step && Array.isArray(step.act) && step.act.length > 0;
      });
    },

    /**
     * Show me: the line's steps not yet met, one after another, each from
     * where it starts, done by Relay the way "Do this step for me" does. The
     * practice server hears of each first (PracticeController.assist), so it
     * grades them as shown whatever the log says (training/assisted).
     */
    async showMe(line) {
      if (this.showingKey || !this.active) return;
      this.showingKey = line.key;
      try {
        for (const id of line.stepIds) {
          if (!this.active) return;
          if (this.isMet(id)) continue;
          const step = watchable(this.stepById(id), this.context);
          if (!step || !Array.isArray(step.act) || !step.act.length) {
            this.statusLine = `Relay can't show "${line.title}". Do it yourself, or choose Guide me step by step.`;
            return;
          }
          if (step.route) {
            const location = await this.resolveRoute(step.route);
            if (location) {
              await Promise.resolve(this.$router.push(location)).catch(() => {});
              await new Promise(resolve => setTimeout(resolve, SETTLE_MS));
            }
          }
          await this.tellAssist(step);
          this.showing.add(id);
          if (!this.runs.has(id)) this.arm(id, { now: true });
          const run = this.runs.get(id);
          if (!run) return;
          run.play();
          const result = await run.result;
          if (result.outcome !== 'done') {
            this.statusLine = `Relay couldn't finish "${line.title}". You can do it yourself.`;
            return;
          }
        }
        this.statusLine = '';
      } finally {
        this.showingKey = null;
      }
    },

    tellAssist(step) {
      if (!this.module) return Promise.resolve();
      return Promise.resolve()
        .then(() =>
          this.$http.post(
            '/training/practice/assist',
            { module_id: this.module.id, step: step.id },
            { timeout: ASSIST_WAIT_MS }
          )
        )
        .catch(() => {});
    },

    /** A step's route, with a load named by PO looked up on this fleet. */
    async resolveRoute(route) {
      const lookup = route && route.params && route.params.id && route.params.id.load;
      if (!lookup) return route;
      const key = typeof lookup === 'string' ? lookup : `po:${lookup.po}`;
      let id = this.loadIdCache[key] || null;
      if (!id && lookup.po) {
        try {
          const res = await this.$http.get('/loads', { params: { delivered: false } });
          const hit = (res.data || []).find(l => l.po === lookup.po);
          id = hit ? hit.id : null;
        } catch (e) {
          id = null;
        }
      }
      if (!id && this.context.loadId) id = this.context.loadId;
      if (id) this.loadIdCache[key] = id;
      return id ? { ...route, params: { ...route.params, id: String(id) } } : null;
    },

    /**
     * Spec 53 5.5's simulated driver, for a line whose card sends one
     * (Deliver): POST /demo/training/drive {load_id}. What the person does
     * with the delivery is what is graded; the drive itself is not.
     */
    async sendDriver() {
      const loadId = Number(this.context.loadId);
      if (!loadId || this.driveState === 'sending' || this.driveState === 'driving') return;
      this.driveState = 'sending';
      this.statusLine = 'Sending the driver...';
      try {
        const res = await this.$http.post('/demo/training/drive', { load_id: loadId });
        const data = (res && res.data) || {};
        if (data.delivered || (data.completion_id && !data.driving)) {
          this.delivered();
        } else {
          this.driveState = 'driving';
          this.statusLine =
            'The driver is on the way. The delivery shows on the load when they arrive.';
          this.lookForDelivery(loadId, Date.now(), Number(data.seconds) || 0);
        }
      } catch (err) {
        const body = err && err.response && err.response.data;
        this.driveState = 'failed';
        this.statusLine =
          (body && typeof body.detail === 'string' && body.detail) ||
          'The driver could not be sent. You can send them again.';
      }
      this.announcement = this.statusLine;
    },

    delivered() {
      clearTimeout(this.driveTimer);
      this.driveState = 'delivered';
      this.statusLine = 'Delivered: the driver has completed the drop.';
      this.announcement = this.statusLine;
    },

    /**
     * READ the load's proof of delivery every few seconds until the drop has
     * its completion, as the cards do (TourHost sendDriver): never a second
     * post, which would start a second drive.
     */
    lookForDelivery(loadId, startedAt, seconds) {
      const limit = seconds * 1000 * 2 + DRIVE_GRACE_MS;
      let drops = null;
      const look = async () => {
        if (!this.active || this.driveState !== 'driving') return;
        try {
          if (!drops) {
            const load = await this.$http.get(`/loads/${loadId}`);
            const stops = (load && load.data && load.data.stops) || [];
            drops = new Set(stops.filter(s => s && s.type === 'drop').map(s => String(s.id)));
          }
          const pod = await this.$http.get(`/loads/${loadId}/pod`);
          if (readCompletions(pod && pod.data).some(c => drops.has(String(c.stop_id)))) {
            this.delivered();
            return;
          }
        } catch (err) {
          // A read that failed is asked again next time, until the limit.
        }
        if (Date.now() - startedAt > limit) {
          this.driveState = 'failed';
          this.statusLine = 'This is taking longer than it should. You can send the driver again.';
          return;
        }
        this.driveTimer = setTimeout(look, DRIVE_POLL_MS);
      };
      this.driveTimer = setTimeout(look, DRIVE_POLL_MS);
    },

    save(extra = {}) {
      if (!this.module) return;
      writeRun({
        tourId: this.definition.id,
        module: this.module.id,
        version: this.module.version,
        startedAt: this.startedAt,
        steps: this.log,
        captured: this.captured,
        finished: false,
        mode: 'free',
        ...extra,
      });
    },

    /** Stop watching: every run, and the request log. */
    stop() {
      clearTimeout(this.driveTimer);
      for (const run of this.runs.values()) run.cancel();
      this.runs.clear();
      this.showing.clear();
      if (this.requests) this.requests.uninstall();
      this.requests = null;
      this.active = false;
      this.showingKey = null;
      this.driveState = null;
    },

    /** Hand the run to the grader and the celebration (TrainingRunHost). */
    finish() {
      if (!this.active || !this.finished) return;
      const steps = this.log.map(entry => ({ ...entry }));
      this.save({ finished: true });
      const detail = {
        reason: 'completed',
        tourId: this.definition.id,
        module: this.module.id,
        version: this.module.version,
        startedAt: this.startedAt,
        steps,
        captured: { ...this.captured },
        result: grade(this.module, steps),
      };
      this.stop();
      window.dispatchEvent(new window.CustomEvent('lw:training-end', { detail }));
    },

    /** The attempt stays open (4.6); a reload does not start it again. */
    leave() {
      if (!this.active) return;
      this.save({ left: true });
      this.stop();
    },

    /** The cards, from the first step not done yet; they keep this log. */
    guideMe() {
      if (!this.active) return;
      const id = this.definition.id;
      const next = this.checklist.find(line => !line.done);
      const stepId = next ? next.stepIds.find(s => !this.isMet(s)) || next.firstStepId : null;
      this.save({ handover: true, context: this.context });
      this.stop();
      const query = { ...((this.$route && this.$route.query) || {}), tour: id };
      if (stepId) query.step = stepId;
      Promise.resolve(this.$router.push({ path: this.$route.path, query })).catch(() => {});
    },
  },
};
</script>

<style scoped>
/*
  The walkthrough card's corner (LwTourCard --corner): bottom right, above
  the page, never over the left rail. A guest on the page, so its buttons are
  secondary and link styles; the page keeps its own primary.
*/
.lw-c-task {
  background: var(--lw-elev-3-surface);
  border: 1px solid var(--lw-elev-3-line);
  border-radius: var(--lw-radius-md);
  bottom: var(--lw-space-5);
  box-shadow: var(--lw-elev-3-shadow);
  color: var(--lw-fg-primary);
  max-height: calc(100vh - var(--lw-space-6) * 2);
  max-width: calc(100vw - var(--lw-space-6));
  overflow-y: auto;
  padding: var(--lw-space-4) var(--lw-space-5);
  position: fixed;
  right: var(--lw-space-5);
  width: 22.5rem;
  z-index: calc(var(--lw-z-toast) * 11);
}

.lw-c-task__head {
  padding-right: var(--lw-space-6);
  position: relative;
}

.lw-c-task__eyebrow {
  color: var(--lw-fg-secondary);
  display: flex;
  font: var(--lw-type-label);
  justify-content: space-between;
  letter-spacing: var(--lw-type-label-tracking);
  margin: 0 0 var(--lw-space-1);
  text-transform: var(--lw-type-label-transform);
}

.lw-c-task__title {
  font: var(--lw-type-subhead);
  margin: 0;
}

.lw-c-task__toggle {
  background: none;
  border: 0;
  color: var(--lw-fg-secondary);
  cursor: pointer;
  padding: var(--lw-space-1);
  position: absolute;
  right: calc(var(--lw-space-2) * -1);
  top: 0;
}

.lw-c-task__toggle:focus-visible {
  outline: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
}

.lw-c-task__body {
  margin-top: var(--lw-space-3);
}

/* The task is the panel's working line: the amber rule the card's instruction carries. */
.lw-c-task__task {
  border-left: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
  font: var(--lw-type-body-sm);
  margin: 0 0 var(--lw-space-2);
  padding-left: var(--lw-space-3);
}

.lw-c-task__how {
  color: var(--lw-fg-secondary);
  font: var(--lw-type-caption);
  margin: 0 0 var(--lw-space-3);
}

.lw-c-task__links {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lw-space-3);
  margin: 0 0 var(--lw-space-3);
}

.lw-c-task__list {
  list-style: none;
  margin: 0 0 var(--lw-space-3);
  padding: 0;
}

.lw-c-task__line {
  display: flex;
  gap: var(--lw-space-3);
  padding: var(--lw-space-2) 0;
}

.lw-c-task__line + .lw-c-task__line {
  border-top: 1px solid var(--lw-elev-3-line);
}

.lw-c-task__mark {
  align-items: center;
  border: 2px solid var(--lw-fg-secondary);
  border-radius: 50%;
  display: inline-flex;
  flex: none;
  height: 1.25rem;
  justify-content: center;
  margin-top: 0.1rem;
  width: 1.25rem;
}

.lw-c-task__line--done .lw-c-task__mark {
  background: var(--lw-success);
  border-color: transparent;
  color: var(--lw-fg-on-accent);
  font-size: 0.7rem;
}

.lw-c-task__line-title {
  font: var(--lw-type-body-sm);
  font-weight: 700;
  margin: 0;
}

.lw-c-task__line--done .lw-c-task__line-title {
  color: var(--lw-fg-secondary);
}

.lw-c-task__hint {
  color: var(--lw-fg-secondary);
  font: var(--lw-type-caption);
  margin: var(--lw-space-1) 0 0;
}

.lw-c-task__line-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lw-space-2);
  margin-top: var(--lw-space-1);
}

.lw-c-task__status {
  font: var(--lw-type-body-sm);
  margin: 0 0 var(--lw-space-3);
}

.lw-c-task__foot {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--lw-space-3);
  justify-content: space-between;
}

.lw-c-task--collapsed .lw-c-task__head {
  margin: 0;
}

.lw-c-task__sr,
.lw-c-task__live {
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

@media (max-width: 40rem) {
  .lw-c-task {
    border-radius: var(--lw-radius-md) var(--lw-radius-md) 0 0;
    bottom: 0;
    left: 0;
    max-height: 60vh;
    right: 0;
    width: auto;
  }
}
</style>
